import { createApi } from "@reduxjs/toolkit/dist/query/react"
import { apiQuery } from "../../utils/api-config";
import { ResponseRole, ResponseRoles } from "../../types/role/ResponseRoles";
import { ModificationDTO } from "../../types/init/modificationDTO";
import { BaseResponse, IikoRole, RoleSearchParams, RoleSearchResponse, ZupRoleRequest, ZupRoleResponse } from "../../features/integration-setting-table/RolesTable/role.type";

export const roleApi = createApi({
    reducerPath: "role",
    baseQuery: apiQuery,
    tagTypes: ["roles", "searchRoles", "unmappedRoles", "getZupRoles", "getIikoRoles"],
    endpoints: builder => ({
        // Получение
        getRoles: builder.query<ResponseRoles, void>({
            query: () => '/roles',
            providesTags: ["roles"]
        }),
        createRole: builder.mutation<ResponseRole, Partial<ModificationDTO>>({
            query: (data) => ({
                url: '/roles',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['roles', 'searchRoles', "unmappedRoles"]
        }),
        updateRole: builder.mutation<ResponseRole, Partial<{ id: number, data: ModificationDTO }>>({
            query: ({ id, data }) => ({
                url: `/roles/${id}`,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['roles', 'searchRoles', "unmappedRoles"]
        }),
        removeRole: builder.mutation<void, Partial<number>>({
            query: id => ({
                url: `/roles/${id}`,
                method: "DELETE"
            }),
            invalidatesTags: ["searchRoles", "unmappedRoles"]
        }),
        searchRoles: builder.query<RoleSearchResponse, RoleSearchParams>({
            query: (params) => ({
                url: '/roles/search',
                method: 'POST',
                body: params
            }),
            providesTags: ["searchRoles"]
        }),
        getZupRoles: builder.query<ZupRoleResponse, ZupRoleRequest>({
            query: (params) => ({
                url: '/1c/roles',
                method: 'POST',
                body: {
                    page_number: params.page_number || 0,
                    page_size: params.page_size || 10,
                    ...params
                },
            }),
            providesTags: ["getZupRoles"]
        }),
        getZupUnmappedRoles: builder.query<ZupRoleResponse, ZupRoleRequest>({
            query: (params) => ({
                url: '/1c/unmapped/roles',
                method: 'POST',
                body: {
                    page_number: params.page_number || 0,
                    page_size: params.page_size || 10,
                    ...params
                },
            }),
            providesTags: ["unmappedRoles"]
        }),
        patchZupRole: builder.mutation<void, { id: string }>({
            query: ({ id }) => ({
                url: `/1c/role/${id}`,
                method: 'PATCH',
            }),
            invalidatesTags: ["getZupRoles"]
        }),
        removeZupRole: builder.mutation<void, string>({
            query: (id) => ({
                url: `/1c/role/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ["getZupRoles"]
        }),
        getIikoRoles: builder.query<BaseResponse<IikoRole>, { enabled?: boolean, name?: string }>({
            query: (params) => ({
                url: '/iiko/roles',
                method: 'GET',
                params,
            }),
            providesTags: ["getIikoRoles"]
        }),
        removeIikoRole: builder.mutation<void, string>({
            query: (code) => ({
                url: `/iiko/role/${code}`,
                method: 'DELETE',
            }),
            invalidatesTags: ["getIikoRoles"]
        }),
        patchIikoRole: builder.mutation<void, string>({
            query: (id) => ({
                url: `/iiko/role/${id}`,
                method: 'PATCH',
            }),
            invalidatesTags: ["getIikoRoles"]
        })
    })
})

export const {
    useGetRolesQuery,
    useCreateRoleMutation,
    useUpdateRoleMutation,
    useRemoveRoleMutation,
    useSearchRolesQuery,
    useGetZupRolesQuery,
    useLazyGetZupRolesQuery,
    useRemoveZupRoleMutation,
    useGetIikoRolesQuery,
    useLazyGetIikoRolesQuery,
    useRemoveIikoRoleMutation,
    useLazyGetZupUnmappedRolesQuery,
    usePatchZupRoleMutation,
    usePatchIikoRoleMutation
} = roleApi
